import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import {
  Search, Users, Loader2, ChevronDown, ChevronUp, Building2, CheckCircle2, XCircle,
  FileText, Mail, MapPin, ShieldCheck, ShieldAlert, Clock, CalendarCheck, Wallet, Hash,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, type VenueManagerResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/venue-managers")({
  component: VenueManagersPage,
});

type VerificationStatus = "approved" | "rejected" | "pending";

const VERIFY_STYLE: Record<string, { badge: string; icon: typeof ShieldCheck; label: string }> = {
  approved: { badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400", icon: ShieldCheck, label: "Verified" },
  rejected: { badge: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400", icon: ShieldAlert, label: "Rejected" },
  pending: { badge: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400", icon: Clock, label: "Pending" },
};

const bookingStatusStyle = (status: string) => {
  const s = status.toLowerCase();
  if (s === "confirmed" || s === "checked_out") return "bg-emerald-100 text-emerald-700";
  if (s === "pending" || s === "manager_approval_pending") return "bg-amber-100 text-amber-700";
  if (s === "cancelled") return "bg-red-100 text-red-700";
  if (s === "checked_in") return "bg-blue-100 text-blue-700";
  return "bg-muted text-muted-foreground";
};

function initials(m: VenueManagerResponse) {
  return `${(m.firstName ?? "").charAt(0)}${(m.lastName ?? "").charAt(0)}`.toUpperCase() || "M";
}

function VenueManagersPage() {
  const [q, setQ] = useState("");
  const [managers, setManagers] = useState<VenueManagerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedVenue, setExpandedVenue] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    setLoading(true);
    adminApi.venueManagers({ pageSize: 100 })
      .then(res => setManagers(res.data))
      .catch(err => setError(err instanceof Error ? err.message : "Failed to load venue managers"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = managers.filter(m => {
    const name = `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim().toLowerCase();
    const term = q.toLowerCase();
    return name.includes(term) || (m.email ?? "").toLowerCase().includes(term) || (m.businessName ?? "").toLowerCase().includes(term);
  });

  const summary = useMemo(() => ({
    total: managers.length,
    verified: managers.filter(m => m.businessVerificationStatus === "approved").length,
    pending: managers.filter(m => m.businessVerificationStatus !== "approved" && m.businessVerificationStatus !== "rejected").length,
    revenue: managers.reduce((s, m) => s + (m.totalRevenue ?? 0), 0),
  }), [managers]);

  const updateVerification = async (manager: VenueManagerResponse, status: VerificationStatus) => {
    const note = status === "rejected" ? prompt("Reason for rejecting these credentials?") ?? undefined : undefined;
    setBusyId(manager.id);
    try {
      const updated = await adminApi.updateVenueManagerVerification(manager.id, { status, note });
      setManagers(prev => prev.map(m => m.id === manager.id ? {
        ...m,
        businessVerificationStatus: updated.businessVerificationStatus,
        businessVerifiedAt: updated.businessVerifiedAt,
        businessVerificationNote: updated.businessVerificationNote,
      } : m));
      toast.success(status === "approved" ? "Manager credentials approved" : status === "rejected" ? "Manager credentials rejected" : "Manager returned to pending review");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update credentials");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Venue Managers</h1>
        <p className="text-sm text-muted-foreground">Review business owners, verify their credentials, and track their properties.</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Managers", value: String(summary.total), icon: Users, tone: "text-foreground" },
          { label: "Verified", value: String(summary.verified), icon: ShieldCheck, tone: "text-emerald-600" },
          { label: "Pending", value: String(summary.pending), icon: Clock, tone: "text-amber-600" },
          { label: "Total Revenue", value: formatNaira(summary.revenue), icon: Wallet, tone: "text-primary" },
        ].map(s => (
          <div key={s.label} className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft">
            <div className="flex items-center justify-between">
              <s.icon className="size-4 text-muted-foreground" />
            </div>
            <p className={`mt-2 font-[family-name:Inter] text-lg font-bold sm:text-xl ${s.tone}`}>{s.value}</p>
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or business…" className="rounded-full pl-9" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading venue managers…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 py-16 text-center text-sm text-destructive">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-20 text-center">
          <Users className="mb-3 size-12 text-muted-foreground/30" />
          <p className="font-semibold text-muted-foreground">
            {managers.length === 0 ? "No venue managers yet" : "No managers match your search"}
          </p>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            {managers.length === 0
              ? "Venue managers will appear here once business accounts are created."
              : "Try adjusting your search terms."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {filtered.map((m) => {
            const vconf = VERIFY_STYLE[m.businessVerificationStatus] ?? VERIFY_STYLE.pending;
            const VIcon = vconf.icon;
            const isOpen = expanded === m.id;
            const hasDetails = m.venues.length > 0 || m.businessName || m.cacNumber || m.ninNumber || m.businessVerificationNote;
            return (
              <div key={m.id} className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft">
                <div className="p-4 sm:p-5">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <div className="grid size-11 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {initials(m)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{`${m.firstName ?? ""} ${m.lastName ?? ""}`.trim() || "Unnamed manager"}</p>
                      <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <Mail className="size-3 shrink-0" /> <span className="truncate">{m.email}</span>
                      </p>
                      {m.businessName && (
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                          <Building2 className="size-3 shrink-0" /> <span className="truncate">{m.businessName}</span>
                        </p>
                      )}
                    </div>
                    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${vconf.badge}`}>
                      <VIcon className="size-3" /> {vconf.label}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-xl bg-muted/50 p-2.5">
                      <p className="text-base font-bold">{m.venueCount}</p>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Venues</p>
                    </div>
                    <div className="rounded-xl bg-muted/50 p-2.5">
                      <p className="text-base font-bold">{m.totalBookings}</p>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Bookings</p>
                    </div>
                    <div className="rounded-xl bg-muted/50 p-2.5">
                      <p className="truncate text-base font-bold text-primary">{formatNaira(m.totalRevenue)}</p>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Revenue</p>
                    </div>
                  </div>

                  {m.location && (
                    <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="size-3.5" /> {m.location}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      disabled={busyId === m.id || m.businessVerificationStatus === "approved"}
                      onClick={() => updateVerification(m, "approved")}
                      className="h-8 flex-1 gap-1 text-xs sm:flex-none"
                    >
                      {busyId === m.id ? <Loader2 className="size-3.5 animate-spin" /> : <CheckCircle2 className="size-3.5" />} Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === m.id || m.businessVerificationStatus === "rejected"}
                      onClick={() => updateVerification(m, "rejected")}
                      className="h-8 flex-1 gap-1 text-xs text-destructive hover:text-destructive sm:flex-none"
                    >
                      <XCircle className="size-3.5" /> Reject
                    </Button>
                    {hasDetails && (
                      <button
                        onClick={() => setExpanded(isOpen ? null : m.id)}
                        className="ml-auto flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5"
                      >
                        {isOpen ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                        {isOpen ? "Hide" : "Details"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {isOpen && (
                  <div className="space-y-4 border-t border-border/60 bg-muted/20 p-4 sm:p-5">
                    {/* Credentials */}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-xl border border-border/40 bg-card p-3 text-xs">
                        <p className="font-semibold text-muted-foreground">Business</p>
                        <p className="mt-1 font-medium">{m.businessName ?? "Not provided"}</p>
                        <p className="text-muted-foreground">{m.businessType ?? "No type"}</p>
                      </div>
                      <div className="rounded-xl border border-border/40 bg-card p-3 text-xs">
                        <p className="font-semibold text-muted-foreground">Review note</p>
                        <p className="mt-1 break-words">{m.businessVerificationNote ?? "No note"}</p>
                      </div>
                      <div className="rounded-xl border border-border/40 bg-card p-3 text-xs">
                        <p className="font-semibold text-muted-foreground">CAC</p>
                        <p className="mt-1 break-words">{m.cacNumber ?? "Not provided"}</p>
                        {m.cacImage && (
                          <button
                            type="button"
                            onClick={() => setModalImage({ src: m.cacImage!, alt: "CAC Certificate" })}
                            className="mt-2 inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <FileText className="size-3" /> View certificate
                          </button>
                        )}
                      </div>
                      <div className="rounded-xl border border-border/40 bg-card p-3 text-xs">
                        <p className="font-semibold text-muted-foreground">NIN</p>
                        <p className="mt-1 break-words">{m.ninNumber ?? "Not provided"}</p>
                        {m.ninImage && (
                          <button
                            type="button"
                            onClick={() => setModalImage({ src: m.ninImage!, alt: "NIN Card" })}
                            className="mt-2 inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <FileText className="size-3" /> View NIN card
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Managed venues */}
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Managed venues ({m.venues.length})
                      </p>
                      {m.venues.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No listings created yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {m.venues.map(v => {
                            const venueOpen = expandedVenue === v.venueId;
                            return (
                              <div key={v.venueId} className="rounded-xl border border-border/40 bg-card p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="flex items-center gap-1.5 truncate text-sm font-semibold">
                                      <Building2 className="size-3.5 shrink-0 text-primary" /> <span className="truncate">{v.venueName}</span>
                                    </p>
                                    <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                                      <MapPin className="size-3 shrink-0" /> {v.location}
                                    </p>
                                  </div>
                                  <div className="shrink-0 text-right">
                                    <p className="text-sm font-bold text-primary">{formatNaira(v.revenue)}</p>
                                    <p className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
                                      <CalendarCheck className="size-3" /> {v.bookingCount}
                                    </p>
                                  </div>
                                </div>
                                {v.recentBookings.length > 0 && (
                                  <>
                                    <button
                                      onClick={() => setExpandedVenue(venueOpen ? null : v.venueId)}
                                      className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                                    >
                                      {venueOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                                      {venueOpen ? "Hide bookings" : `Recent bookings (${v.recentBookings.length})`}
                                    </button>
                                    {venueOpen && (
                                      <ul className="mt-2 space-y-2 border-t border-border/40 pt-2">
                                        {v.recentBookings.map(b => (
                                          <li key={b.bookingId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
                                            <span className="flex items-center gap-1 font-mono text-muted-foreground">
                                              <Hash className="size-2.5" />{b.bookingRef}
                                            </span>
                                            <span className="truncate font-medium">{b.guestName}</span>
                                            <span className="text-muted-foreground">
                                              {new Date(b.startDate).toLocaleDateString("en-NG", { day: "2-digit", month: "short" })} · {b.daysCount}d
                                            </span>
                                            <span className="font-semibold">{formatNaira(b.totalAmount)}</span>
                                            <span className={`rounded-full px-2 py-0.5 font-semibold uppercase ${bookingStatusStyle(b.status)}`}>
                                              {b.status.replace(/_/g, " ")}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Image Lightbox Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative max-w-3xl overflow-hidden rounded-2xl bg-card p-2 shadow-2xl animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setModalImage(null)}
              className="absolute right-3 top-3 z-10 grid size-8 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
              aria-label="Close modal"
            >
              ✕
            </button>
            <img
              src={modalImage.src}
              alt={modalImage.alt}
              className="max-h-[80vh] w-full object-contain rounded-xl"
            />
            <div className="p-3 text-center text-sm font-semibold">
              {modalImage.alt}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
